import { useCallback, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { ZButton } from "@/shared/components/button";
import { useCustomizeControlsStore, getCustomizeControlsActions, type VendorAssessmentControl } from "@/modules/agent/states/cusomize-controls";
import { AgentSessionType } from "@/modules/sessions/types";
import CustomizeControlSearchBar from "@/modules/agent/components/customize-controls/CustomizeControlTopSection/CustomizeControlSearchBar";
import { Dropdown, DropdownMenu, DropdownTrigger, DropdownItem } from "@nextui-org/react";
import { MoreHorizontal, X } from "lucide-react";
import { getRandomCharacter } from "@/modules/agent/components/customize-controls/helpers";
import ImportModal from "@/modules/agent/components/customize-controls/ImportModal";
import { addNotification } from "@/shared/states/notification";
import { convertSnakeToTitleCase } from "@/modules/agent/utils/snake-to-capital";
import { getCustomControlSets, type ControlSet, updateControlSetControls } from "../../requests";
import parsePromise from "@/shared/utils/parsePromise";
import { uploadFileReq } from "@/shared/requests/upload-file";
import { checkAndRefreshUrl } from "@/modules/agent/utils/refreshUrl";

const ControlSetHeader = () => {
  const [searchParams] = useSearchParams();
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [controlSets, setControlSets] = useState<ControlSet[]>([]);
  const [isLoadingControlSets, setIsLoadingControlSets] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const dataState = useCustomizeControlsStore().dataState;
  const selectedControls = useCustomizeControlsStore().selectedControls;
  const selectedTab = useCustomizeControlsStore().selectedTab;
  const showCheckboxes = useCustomizeControlsStore().showCheckboxes;
  const agentType = useCustomizeControlsStore().agentType;

  // Get control set edit context from store
  const { controlSetEditContext } = useCustomizeControlsStore();
  const controlSetId = controlSetEditContext?.controlSetId;
  const controlSetName = controlSetEditContext?.controlSetName || 'Control Set';
  const isNewControlSet = controlSetEditContext?.isNewControlSet || false;
  
  // Dynamic control count
  const controlCount = dataState.length;

  // Fetch control sets when component mounts
  useEffect(() => {
    fetchControlSets();
  }, []);

  const fetchControlSets = async () => {
    setIsLoadingControlSets(true);
    try {
      const [error, response] = await parsePromise(getCustomControlSets());
      if (error) {
        addNotification({
          type: "error",
          title: "Failed to fetch control sets",
          message: error?.message || "Something went wrong. Please try again later!",
        });
        return;
      }
      
      if (response?.success && response.control_sets?.multi_file_vendor_assessment) {
        setControlSets(response.control_sets.multi_file_vendor_assessment);
      }
    } catch (error) {
      console.error("Error fetching control sets:", error);
      addNotification({
        type: "error",
        title: "Failed to fetch control sets",
        message: "Something went wrong. Please try again later!",
      });
    } finally {
      setIsLoadingControlSets(false);
    }
  };

  const handleOnClickAddButton = useCallback(() => {
    // For agent types with persistent empty rows, just focus on the existing empty row
    if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT ||
        agentType === AgentSessionType.SOC2_GAP_TYPE1 ||
        agentType === AgentSessionType.SOC2_GAP_TYPE2) {
      
      setTimeout(() => {
        const table = document.getElementById("customize-control-table");
        if (table) {
          // For MULTI_FILE_VENDOR_ASSESSMENT, look for category input
          if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
            const categoryInputs = table.querySelectorAll('input[id*="key"]');
            let targetInput: HTMLElement | null = null;
            
            for (let input of categoryInputs) {
              const inputEl = input as HTMLInputElement;
              if (inputEl.value === "" || inputEl.value.trim() === "") {
                // Check if this input is in a row that appears to be empty
                const row = inputEl.closest('tr');
                if (row) {
                  const questionInput = row.querySelector('textarea[id*="question"]') as HTMLTextAreaElement;
                  
                  if (questionInput && questionInput.value.trim() === "") {
                    targetInput = inputEl;
                    break;
                  }
                }
              }
            }
            
            if (targetInput) {
              targetInput.focus();
              targetInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
              return;
            }
          }
          
          // Fallback: find the last row and focus on the FIRST input in that row
          const rows = table.querySelectorAll("tbody tr");
          if (rows.length > 0) {
            const lastRow = rows[rows.length - 1];
            
            // For vendor assessment, look for category input first
            if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
              const categoryInput = lastRow.querySelector('input[id*="key"]') as HTMLElement;
              if (categoryInput) {
                categoryInput.focus();
                categoryInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
                return;
              }
            }
            
            // Generic fallback: first input/textarea in the last row
            const firstInput = lastRow.querySelector("input, textarea") as HTMLElement;
            if (firstInput) {
              firstInput.focus();
              firstInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
          }
        }
      }, 100);
      return;
    }

    // For other agent types, use the existing placeholder mechanism
    const uid = getRandomCharacter();
    getCustomizeControlsActions().setNewControlPlaceHolderUid(uid);
    
    setTimeout(() => {
      const table = document.getElementById("customize-control-table");
      if (table) {
        // Fallback: find the last row and focus on first input
        const rows = table.querySelectorAll("tbody tr");
        if (rows.length > 0) {
          const lastRow = rows[rows.length - 1];
          const firstInput = lastRow.querySelector("input, textarea") as HTMLElement;
          if (firstInput) {
            firstInput.focus();
            firstInput.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        }
      }
    }, 150);
  }, [agentType]);

  const toggleCheckboxes = useCallback(() => {
    getCustomizeControlsActions().setShowCheckboxes(!showCheckboxes);
    if (showCheckboxes) {
      // Clear selection when hiding checkboxes
      getCustomizeControlsActions().setSelectedControls(new Set());
    }
  }, [showCheckboxes]);

  const handleOnClickDeleteButton = useCallback(async () => {
    if (!controlSetId || (!controlSetEditContext?.controlsUrl && !controlSetEditContext?.isNewControlSet)) {
      addNotification({
        type: "error",
        title: "Delete failed",
        message: "Missing control set information. Please try again.",
      });
      return;
    }

    setIsDeleting(true);
    
    try {
      // Get current data state before filtering
      const currentData = useCustomizeControlsStore.getState().dataState;
      
      // Filter out selected controls
      const remainingControls = currentData.filter((control) => !selectedControls.has(control.uid));
      
      // Transform remaining controls to save format (same logic as save function)
      const controlsToSave = remainingControls
        .filter(control => {
          // Filter out empty controls - for vendor assessment check category and question
          if (control.agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
            const vendorControl = control as VendorAssessmentControl;
            return vendorControl.category && vendorControl.category.trim().length > 0 &&
                   vendorControl.question && vendorControl.question.trim().length > 0;
          }
          return false; // Only handle vendor assessment for now
        })
        .map(control => {
          // Transform data structure for vendor assessment
          if (control.agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
            const vendorControl = control as VendorAssessmentControl;
            return {
              ...vendorControl,
              key: vendorControl.category, // Map category to key for backend
              category: undefined,         // Remove category field
              new_question: undefined,     // Remove temporary fields
              id: undefined,              // Remove temporary id
              selected: true              // Add selected flag
            };
          }
          return control;
        });

      // Create JSON blob with remaining controls
      const transformedControlsJson = JSON.stringify(controlsToSave, null, 2);
      const blob = new Blob([transformedControlsJson], { type: "application/json" });

      // For new control sets, use importedControlsUrl; for existing ones use controlsUrl
      const targetUrl = controlSetEditContext?.isNewControlSet 
        ? controlSetEditContext?.importedControlsUrl 
        : controlSetEditContext?.controlsUrl;

      if (!targetUrl) {
        throw new Error('No controls URL found for updating');
      }

      // Upload blob and update control set
      const validSignedUrl = await checkAndRefreshUrl(targetUrl);
      await uploadFileReq(validSignedUrl, blob);

      // Only call updateControlSetControls for existing control sets (not new ones)
      if (!controlSetEditContext?.isNewControlSet) {
        const [error] = await parsePromise(
          updateControlSetControls(controlSetId, {
            controls_url: targetUrl,
            operation_type: "update_control_set_controls",
          })
        );

        if (error) throw error;
      }

      // Update UI state after successful blob update
      getCustomizeControlsActions().setDataState(remainingControls);
      getCustomizeControlsActions().setSelectedControls(new Set<string>());
      getCustomizeControlsActions().setChangesMade((prev) => prev + 1);
      getCustomizeControlsActions().setShowCheckboxes(false);
      
      addNotification({
        message: `${selectedControls.size} control${
          selectedControls.size > 1 ? "s" : ""
        } has been removed from ${convertSnakeToTitleCase(
          selectedTab ? selectedTab : ""
        )}`,
        type: "success",
      });

    } catch (error) {
      console.error('Error deleting controls:', error);
      addNotification({
        type: "error",
        title: "Delete failed",
        message: "Could not delete controls. Please try again.",
      });
    } finally {
      setIsDeleting(false);
    }
  }, [selectedControls, selectedTab, controlSetId, controlSetEditContext]);

  const handleOpenImportModal = useCallback(() => {
    setIsImportModalOpen(true);
  }, []);

  const handleCloseImportModal = useCallback(() => {
    setIsImportModalOpen(false);
  }, []);

  const handleImportSuccess = useCallback(async (files?: any[], controlsData?: any[], signedUrl?: string) => {
    // If we have controls data (from import), load it into the data state
    if (controlsData && Array.isArray(controlsData)) {
      const { setDataState, setControlSetEditContext } = getCustomizeControlsActions();
      
      // Step 1: Clear old state immediately
      setDataState([]);
      
      // Step 2: Small delay to ensure UI updates with empty state
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Step 3: Transform controls to match VendorAssessmentControl interface
      const transformedControls: VendorAssessmentControl[] = controlsData.map((control: any) => ({
        ...control,
        agentType: AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT,
        uid: control.uid || control.id || `imported_${Math.random().toString(36).substr(2, 9)}`,
        category: control.category || control.key || '',
        question: control.question || '',
        section: control.section || 'zania_security_assessment' as const,
        edited_state: 'default' as const,
        changesInQuestion: 0,
      }));

      // Step 4: Update data state with imported controls
      setDataState(transformedControls);
      
      // Only update URLs if we have a signedUrl AND it's from an Excel import (not from pre-existing control set)
      // When importing from pre-existing control sets, signedUrl should be undefined
      // When importing from Excel file, signedUrl will be the temporary uploaded file URL
      if (signedUrl) {
        const currentContext = useCustomizeControlsStore.getState().controlSetEditContext;
        if (currentContext) {
          setControlSetEditContext({
            ...currentContext,
            // Only update controlsUrl if it's not already set (new control set scenario)
            // Otherwise preserve the existing controlsUrl to avoid overwriting target with source URL
            controlsUrl: currentContext.controlsUrl || signedUrl,
            importedControlsUrl: signedUrl
          });
        }
      }
    }
    
    // Force an update to ensure data is refreshed after import
    getCustomizeControlsActions().setChangesMade(prev => prev + 1);
    // Also refresh control sets list in case new ones were created
    await fetchControlSets();
    setIsImportModalOpen(false);
  }, []);

  return (
    <>
      <div className="px-6 py-4 grid grid-cols-3 min-h-[69px] bg-white gap-2">
        {/* Left: Control Set Name/Count OR Close Button with Selection Text */}
        <div className="col-span-1 items-center my-auto">
          {showCheckboxes ? (
            <div className="flex items-center gap-2">
              <ZButton
                variant="light"
                className="p-1 h-8 w-8 min-w-8"
                onClick={toggleCheckboxes}
              >
                <X size={16} />
              </ZButton>
              <div className="text-sm font-medium">
                {`${selectedControls.size} control${selectedControls.size !== 1 ? 's' : ''} selected for deletion`}
              </div>
            </div>
          ) : (
            <div className="flex items-center">
              <div className="text-sm font-medium">
                {controlSetName}
              </div>
              <div className="p-1 bg-[#F4F4F5] px-2 rounded-lg ml-2">
                <p className="text-[10px] text-[#52525B] leading-4 font-medium">
                  {controlCount}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Center: Search Bar */}
        <div className="col-span-1">
          <CustomizeControlSearchBar />
        </div>
        
        {/* Right: Delete Button (delete mode) OR Add Control + Kebab Menu (normal mode) */}
        <div className="col-span-1 flex justify-end items-center my-auto">
          <div className="flex flex-row gap-1">
            {showCheckboxes ? (
              /* Delete mode: Only show Delete Controls Button */
              <ZButton
                color="danger"
                variant="solid"
                className="text-xs font-medium max-h-7 px-3 text-white"
                onClick={handleOnClickDeleteButton}
                isDisabled={selectedControls.size === 0 || isDeleting}
                isLoading={isDeleting}
              >
                Delete Control{selectedControls.size > 1 ? 's' : ''}
              </ZButton>
            ) : (
              /* Normal mode: Show Add Control + Kebab Menu */
              <>
                <ZButton
                  color="primary"
                  variant="bordered"
                  className="text-xs font-medium max-h-7 max-w-32 border-1"
                  onClick={handleOnClickAddButton}
                  onMouseDown={handleOnClickAddButton}
                >
                  Add Control
                </ZButton>
                
                <Dropdown 
                    placement="bottom-end"
                    classNames={{
                      content: "min-w-[140px] p-0 rounded-md"
                    }}
                  >
                    <DropdownTrigger>
                      <div className="cursor-pointer p-1 ml-1.5 hover:bg-gray-100 rounded-full">
                        <MoreHorizontal className="h-4 w-4 text-gray-600" />
                      </div>
                    </DropdownTrigger>
                    <DropdownMenu 
                    aria-label="Control Actions"
                    className="p-0 rounded-sm"
                    itemClasses={{
                      base: "py-2.5 px-3 text-sm",
                      description: "text-foreground",
                      title: "text-foreground",
                    }}
                  >
                    <DropdownItem 
                      key="import" 
                      className="text-xs rounded-none" 
                      classNames={{
                        base: "data-[hover=true]:bg-gray-100 rounded-none",
                      }}
                      onClick={handleOpenImportModal}
                    >
                      <div>Import Controls</div>
                    </DropdownItem>

                    <DropdownItem 
                      key="delete" 
                      className="text-danger text-xs rounded-none" 
                      classNames={{
                        base: "text-danger data-[hover=true]:bg-gray-100 data-[hover=true]:text-danger rounded-none",
                        description: "text-danger",
                        title: "text-danger",
                      }}
                      onClick={() => {
                        if (!showCheckboxes) {
                          toggleCheckboxes();
                        }
                      }}
                    >
                      <div className="text-danger">Delete Controls</div>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Import Modal */}
      <ImportModal
        isOpen={isImportModalOpen}
        onClose={handleCloseImportModal}
        onImport={handleImportSuccess}
        sessionId={controlSetId || ""}
        isControlSetEdit={true}
        isAgentTypeMultiFile={true}
        controlSets={controlSets}
        isLoadingControlSets={isLoadingControlSets}
      />
    </>
  );
};

export default ControlSetHeader;